import { Component, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

const GOLD = "#C9A227";
const goldMat = (
  <meshStandardMaterial color={GOLD} metalness={0.85} roughness={0.28} />
);

function Particles({ color, count }) {
  const ref = useRef();
  const matRef = useRef();
  const target = useMemo(() => new THREE.Color(color), [color]);
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = Math.random() * 0.62;
      const t = Math.random() * Math.PI * 2;
      arr[i * 3] = Math.cos(t) * r;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 1.5;
      arr[i * 3 + 2] = Math.sin(t) * r;
    }
    return arr;
  }, [count]);

  useFrame((_, delta) => {
    if (ref.current) ref.current.rotation.y += delta * 0.25;
    if (matRef.current) matRef.current.color.lerp(target, 0.06);
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial
        ref={matRef}
        size={0.035}
        transparent
        opacity={0.9}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        color={color}
      />
    </points>
  );
}

function Lantern({ accent, staticMode, particleCount }) {
  const group = useRef();
  const glass = useRef();
  const light = useRef();
  const core = useRef();
  const mouse = useRef({ x: 0, y: 0 });
  const target = useMemo(() => new THREE.Color(accent), [accent]);
  const ribs = useMemo(
    () => Array.from({ length: 8 }, (_, i) => (i * Math.PI) / 4),
    []
  );

  useEffect(() => {
    const handler = (e) => {
      mouse.current.x = (e.clientX / window.innerWidth - 0.5) * 2;
      mouse.current.y = -(e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener("mousemove", handler, { passive: true });
    return () => window.removeEventListener("mousemove", handler);
  }, []);

  useFrame((state, delta) => {
    if (!group.current) return;
    const t = state.clock.elapsedTime;
    if (!staticMode) {
      const px = mouse.current.x;
      const py = mouse.current.y;
      group.current.position.x = THREE.MathUtils.lerp(
        group.current.position.x,
        px * 0.55,
        0.05
      );
      group.current.position.y = THREE.MathUtils.lerp(
        group.current.position.y,
        py * 0.35 + Math.sin(t * 0.9) * 0.07,
        0.05
      );
      group.current.rotation.y += delta * 0.18;
      group.current.rotation.z = THREE.MathUtils.lerp(
        group.current.rotation.z,
        px * -0.06,
        0.05
      );
    }
    if (glass.current) {
      glass.current.material.emissive.lerp(target, 0.05);
      glass.current.material.color.lerp(target, 0.05);
    }
    if (light.current) light.current.color.lerp(target, 0.05);
    if (core.current) {
      core.current.material.color.lerp(target, 0.05);
      core.current.scale.setScalar(1 + Math.sin(t * 3) * 0.08);
    }
  });

  return (
    <group ref={group}>
      <mesh position={[0, -1.06, 0]}>
        <cylinderGeometry args={[1.04, 1.12, 0.14, 8]} />
        {goldMat}
      </mesh>
      <mesh position={[0, -1.16, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[1.05, 0.045, 12, 32]} />
        {goldMat}
      </mesh>
      <mesh ref={glass} position={[0, -0.2, 0]}>
        <cylinderGeometry args={[0.86, 1.0, 1.6, 8, 1, true]} />
        <meshPhysicalMaterial
          color={accent}
          emissive={accent}
          emissiveIntensity={0.55}
          transparent
          opacity={0.32}
          roughness={0.08}
          metalness={0}
          side={THREE.DoubleSide}
          depthWrite={false}
        />
      </mesh>
      {ribs.map((a, i) => (
        <mesh
          key={i}
          position={[Math.cos(a) * 0.93, -0.2, Math.sin(a) * 0.93]}
          rotation={[0, -a, 0]}
        >
          <boxGeometry args={[0.05, 1.72, 0.05]} />
          {goldMat}
        </mesh>
      ))}
      <mesh position={[0, 0.62, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.88, 0.045, 12, 32]} />
        {goldMat}
      </mesh>
      <mesh position={[0, 1.0, 0]}>
        <coneGeometry args={[0.92, 0.72, 8]} />
        {goldMat}
      </mesh>
      <mesh position={[0, 1.42, 0]}>
        <sphereGeometry args={[0.09, 16, 16]} />
        {goldMat}
      </mesh>
      <mesh position={[0, 1.62, 0]}>
        <torusGeometry args={[0.14, 0.03, 10, 24]} />
        {goldMat}
      </mesh>
      <mesh ref={core} position={[0, -0.2, 0]}>
        <sphereGeometry args={[0.22, 16, 16]} />
        <meshBasicMaterial color={accent} transparent opacity={0.95} />
      </mesh>
      <Particles color={accent} count={particleCount} />
      <pointLight
        ref={light}
        position={[0, -0.1, 0]}
        color={accent}
        intensity={14}
        distance={9}
        decay={2}
      />
    </group>
  );
}

function LanternFallback({ accent }) {
  return (
    <div
      className="flex h-full items-center justify-center"
      data-testid="lantern-fallback"
    >
      <svg width="240" height="330" viewBox="0 0 200 280" fill="none" aria-hidden>
        <defs>
          <radialGradient id="lantern-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={accent} stopOpacity="0.85" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="100" cy="145" r="95" fill="url(#lantern-glow)" opacity="0.55" />
        <g stroke={GOLD} strokeWidth="2" strokeLinejoin="round">
          <path d="M60 112 L140 112 L131 202 L69 202 Z" />
          <path d="M100 72 L138 112 L62 112 Z" />
          <rect x="70" y="202" width="60" height="10" rx="3" />
          <circle cx="100" cy="62" r="7" />
        </g>
        <circle cx="100" cy="157" r="16" fill={accent} />
      </svg>
    </div>
  );
}

class Boundary extends Component {
  constructor(props) {
    super(props);
    this.state = { err: false };
  }
  static getDerivedStateFromError() {
    return { err: true };
  }
  render() {
    return this.state.err ? this.props.fallback : this.props.children;
  }
}

export default function LanternScene({ accent = "#E8C98A" }) {
  const [cfg] = useState(() => ({
    mobile: window.innerWidth < 768,
    reduced: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  }));
  const particleCount = cfg.mobile ? 60 : 160;

  if (cfg.reduced) return <LanternFallback accent={accent} />;

  return (
    <Boundary fallback={<LanternFallback accent={accent} />}>
      <Canvas
        dpr={[1, cfg.mobile ? 1.25 : 1.75]}
        camera={{ position: [0, 0.1, 5.6], fov: 38 }}
        gl={{
          antialias: true,
          alpha: true,
          powerPreference: "high-performance",
        }}
        style={{ pointerEvents: "none" }}
        data-testid="lantern-canvas"
      >
        <ambientLight intensity={0.35} />
        <directionalLight position={[3, 5, 4]} intensity={1.1} color="#FFE9C4" />
        <Lantern
          accent={accent}
          staticMode={false}
          particleCount={particleCount}
        />
      </Canvas>
    </Boundary>
  );
}
